import { getAPIData, apiRequest } from "./axios";
import { API_PATHS } from "../../config/apiConfig";
import { getDateString } from "../../utils/datetime";

// Getters
export async function getWeatherData(date, jobAddress) {
  const url = API_PATHS.DISPATCH.WEATHER_DATA(date, jobAddress);
  return await getAPIData(url);
}

/**
 * Fetches DayDetails for each calendar day from `startIso` through `endIso` (inclusive).
 * Dates must be `YYYY-MM-DD` (local calendar keys).
 *
 * @param {string} startIso
 * @param {string} endIso
 * @returns {Promise<Array>}
 */
export async function getDayDetails(startIso, endIso) {
  const qs = new URLSearchParams({ start: startIso, end: endIso });
  const url = `${API_PATHS.DISPATCH.DAY_DETAILS}?${qs.toString()}`;
  return await getAPIData(url);
}

export async function getModelReferenceData() {
  const url = API_PATHS.DISPATCH.MODEL_REFERENCE;
  return await getAPIData(url);
}

/**
 * Fetch full project concrete-pour entries (filter Type/Range on the client).
 *
 * @param {Object} params
 * @param {number|string} params.job - JobNumber primary key (required).
 * @param {Object} [config] - Optional axios config (e.g. ``signal`` for abort).
 * @returns {Promise<Object>} ``{ job, entries }``.
 */
export async function getProjectConcretePours({ job } = {}, config = {}) {
  const params = new URLSearchParams();
  if (job != null) {
    params.set("job", String(job));
  }
  const query = params.toString();
  const url = `${API_PATHS.DISPATCH.PROJECT_CONCRETE_POURS}${query ? `?${query}` : ""}`;
  return await apiRequest(url, null, { method: "GET", ...config });
}

// Setters
export async function addWorkScopeAspect(scope) {
  const url = API_PATHS.DISPATCH.ADD_WORK_SCOPE;
  const body = { scope };
  return await apiRequest(url, body, { method: "POST" });
}

export async function duplicateScopes(type, origin, targetJobIds, targetDate) {
  const url = API_PATHS.DISPATCH.DUPLICATE_SCOPES;
  const body = { type, origin, targetJobIds, targetDate };
  return await apiRequest(url, body, { method: "POST" });
}

export async function patchDayDetails(date, workerIds) {
  const url = API_PATHS.DISPATCH.PATCH_DAY_DETAILS(date);
  const body = { workerIds };
  return await apiRequest(url, body, { method: "PATCH" });
}

export async function updateScopeDetails(scopeId, payload) {
  const url = API_PATHS.DISPATCH.UPDATE_SCOPE_DETAILS;
  const body = { scopeId, payload };
  return await apiRequest(url, body, { method: "POST" });
}

export async function updateWorkScopeAspect(scopeId, payload) {
  const url = API_PATHS.DISPATCH.UPDATE_WORK_SCOPE;
  const body = { scopeId, payload };
  return await apiRequest(url, body, { method: "POST" });
}

export async function updateDispatchStage(stage, date, checks = null) {
  const url = API_PATHS.DISPATCH.UPDATE_DISPATCH_STAGE;
  const body = { stage, checks, date };
  return await apiRequest(url, body, { method: "POST" });
}

// Deleters
export async function clearAssignmentSet(scopeId, setType) {
  const url = API_PATHS.DISPATCH.CLEAR_ASSIGNMENT_SET;
  const body = { scopeId, setType };
  return await apiRequest(url, body, { method: "POST" });
}

export async function clearWorkers(date) {
  const url = API_PATHS.DISPATCH.CLEAR_WORKERS;
  const body = { date };
  return await apiRequest(url, body, { method: "POST" });
}

export async function deleteWorkScopeAspects(scopeIds) {
  const url = API_PATHS.DISPATCH.DELETE_WORK_SCOPE_ASPECTS;
  const body = { scopeIds };
  return await apiRequest(url, body, { method: "POST" });
}

// Legacy functions from dispatchv2.js - These are outdated API calls that were made by Ben. 
// Some of them are still in use. These should not be used in new code. 
// Use the new API calls above instead. If something doesn't exist there, build it there. 
// These will be removed in the future.

export async function getJobsByWeek(weekStart) {
  const weekStartStr = getDateString(weekStart);
  const url = `/dispatch/getjobs/${weekStartStr}`;
  return await apiRequest(url, null, { method: "GET" });
}

export async function getScopesByWeek(weekStart) {
  const weekStartStr = getDateString(weekStart);
  const url = `/dispatch/getscopes/${weekStartStr}`;
  return await apiRequest(url, null, { method: "GET" });
}

export async function deleteScope(scopeId) {
  const url = `/dispatch/workscopeaspects/${scopeId}/`;
  return await apiRequest(url, null, { method: "DELETE" });
}


export async function getScope(scopeId) {
  const url = `/dispatch/workscopeaspects/${scopeId}/`;
  return await apiRequest(url, null, { method: "GET" });
}


// Partial aspect updates live in services/api/planning.js (promoted cross-module API).

// Some requests used to fill in forms

export async function getMixDesigns() {
  return await apiRequest("/dispatch/mixdesigns/", null, { method: "GET" });
}

export async function getDumpingSuppliers() {
  return await apiRequest("/dispatch/dumpingsuppliers/", null, {
    method: "GET",
  });
}

export async function getAssets() {
  return await apiRequest("/dispatch/assets/", null, { method: "GET" });
}

export async function getsubcontractor() {
  return await apiRequest("/dispatch/subcontractor/", null, { method: "GET" });
}

export async function getWorkScopes() {
  return await apiRequest("/dispatch/workscopes/", null, { method: "GET" });
}

export async function getAddresses() {
  return await apiRequest("/dispatch/addresses/", null, { method: "GET" });
}

